import { defineComponent as _defineComponent } from 'vue'
import { onMounted, ref } from "vue";
import ForceGraph from "force-graph";
import AccountAddress from "@/components/common/AccountAddress.vue";

// props
interface IProps {
	mint: string;
	token: object;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'InsiderGraph',
  props: {
    mint: null,
    token: null
  },
  setup(__props: any) {

const props = __props as {
	mint: string;
	token: object;
};



// data
const graph2D = ref<any>(ForceGraph());
const graphData = ref<any>(null);
const el2D = ref<HTMLElement | null>(null);

const hoverNode = ref<any | null>(null);
const highlightNodes = ref<Set<any>>(new Set());
const highlightLinks = ref<Set<any>>(new Set());

const NODE_R = 12;

const fmtComp = Intl.NumberFormat("en", { notation: "compact" });

// methods
async function init(): Promise<void> {
	el2D.value = document.getElementById("insider-graph2d");

	fetchGraphData();
	// await fetchGraphData();
}

function fetchGraphData() {
	const uri = `https://api.rugcheck.xyz/v1/tokens/${props.mint}/insiders/graph`;
	fetch(uri)
		.then((r) => r.json())
		.then(processResponse);
}

function processResponse(data) {
	const joined = {
		nodes: [],
		links: [],
	};

	// Use Maps to track unique nodes and links
	const nodesMap = new Map();
	const linksMap = new Map();

	// Process all networks
	for (let i = 0; i < data.length; i++) {
		const networkId = data[i].net_id;
		const networkType = data[i].network_type;

		// Process nodes
		data[i].nodes.forEach((node) => {
			// Add network ID to node
			node.net_id = networkId;
			node.net_type = networkType;

			// Only add if not already in the map
			if (!nodesMap.has(node.id)) {
				nodesMap.set(node.id, node);
			}
		});

		// Process links
		data[i].links.forEach((link) => {
			// Use source-target combination as unique key for links
			// Assuming link has source and target properties
			const linkKey = `${link.source}-${link.target}`;

			// Only add if not already in the map
			if (!linksMap.has(linkKey)) {
				linksMap.set(linkKey, link);
			}
		});
	}

	// Convert maps back to arrays for the final joined object
	joined.nodes = Array.from(nodesMap.values());
	joined.links = Array.from(linksMap.values());

	graphData.value = joined;

	if (joined.nodes.length === 0) return; //Nothing to build

	// Initialize the graph with the merged data
	initGraph(joined);
}

function initGraph(data) {
	data = buildDataObject(data);

	const el = el2D.value;
	console.log("TEST", el.clientHeight, el.clientWidth);
	graph2D.value.backgroundColor("black");
	graph2D
		.value(el)
		.graphData(data) //Add our dataset
		.width(el.clientWidth)
		.height(el.clientHeight)
		.backgroundColor("#000000")
		.nodeRelSize(NODE_R)
		.nodeLabel(nodeLabel)
		.nodeAutoColorBy((n) => `${n.net_id}-${n.participant}`)
		.linkAutoColorBy("source")
		.onNodeClick(onNodeClick)
		.onNodeHover((n) => onNodeHover(n))
		.onLinkHover(onLinkHover)
		.d3VelocityDecay(0.2)
		.cooldownTicks(200)
		.warmupTicks(100)
		.linkWidth((l) =>
			highlightLinks.value.has(l)
				? 5
				: l.target.participant && l.source.participant
				? 2
				: 1
		)
		.linkDirectionalParticles(2)
		.linkDirectionalParticleColor("white")
		.linkDirectionalParticleWidth((link) =>
			highlightLinks.value.has(link) ? 4 : 0
		)
		.linkColor(() => "rgba(100,100,100,0.4)")
		.nodeCanvasObject(drawNode);

	graph2D.value(el).d3Force("center", null);
	graph2D.value(el).d3Force("link").strength(0.08);

	// fit to canvas when engine stops
	setTimeout(() => {
		graph2D.value.zoomToFit(400)
	}, 400)

	observeVisibility(el2D.value, (isVisible) => {
		if (isVisible) {
			graph2D.value
				.width(el2D.value.clientWidth)
				.height(el2D.value.clientHeight);
		}
	});

	window.addEventListener(
		"resize",
		() => {
			graph2D.value
				.width(el2D.value.clientWidth)
				.height(el2D.value.clientHeight);
		},
		false
	);
}

//Builds a list of neighbors & links for highlighting later
function buildDataObject(data) {
	if (!data?.links) data.links = [];
	if (!data?.nodes) data.nodes = [];

	const nMap = {};
	data.nodes.forEach((n) => (nMap[n.id] = n));

	// cross-link node objects
	data.links.forEach((link) => {
		const a = nMap[link.source];
		const b = nMap[link.target];

		if (!a || !b) return;

		!a.neighbors && (a.neighbors = []);
		!b.neighbors && (b.neighbors = []);

		a.neighbors.push(b);
		b.neighbors.push(a);

		!a.links && (a.links = []);
		!b.links && (b.links = []);
		a.links.push(link);
		b.links.push(link);
	});

	data.nodes = Object.values(nMap);
	return data;
}

function knownAccount(input: string): string {
	if (input == props.token?.createdBy) {
		return "Creator";
	}

	//TODO Check if known acc

	return input.substring(0, 3);
}

function onNodeClick(node, e) {
	console.log("onNodeClick", node);

	let tab = "balanceChanges";
	if (node.holdings) {
		tab = "transfers";
	} else if (node.net_type === "trade")
		tab = node.participant ? "defiactivities" : "";

	window
		.open(
			`https://solscan.io/account/${node.name || node.id}?token_address=${
				props.mint
			}#${tab}`,
			"_blank"
		)
		.focus();
}

function onNodeHover(node) {
	if (node === hoverNode.value) return;

	highlightNodes.value.clear();
	highlightLinks.value.clear();
	if (node) {
		highlightNodes.value.add(node);
		node.neighbors.forEach((neighbor) => highlightNodes.value.add(neighbor));
		node.links.forEach((link) => highlightLinks.value.add(link));
	}

	hoverNode.value = node || null;
}

function onLinkHover(link, prevLink) {
	highlightNodes.value.clear();
	highlightLinks.value.clear();

	if (link) {
		highlightLinks.value.add(link);
		highlightNodes.value.add(link.source);
		highlightNodes.value.add(link.target);
	}
}

function nodeLabel(node) {
	let accName = `${node.id.substr(0, 4)}...${node.id.substr(-4)}`;
	let customName = knownAccount(node.id);
	if (customName != node.id.substr(0, 3)) {
		accName = customName;
	}

	let action;
	switch (node.net_type) {
		case "transfer":
			action = "XFER";
			break;
		case "trade":
			action = "TRADE";
			break;
		default:
			action = node.net_type.toUpperCase();
	}

	return `${action}: ${accName} ${
		node.holdings
			? `- ${fmtComp.format(
					node.holdings / Math.pow(10, props.token?.decimals)
			  )}`
			: ""
	}`;
}

function drawNode(node, ctx, globalScale) {
	let nodeScale = NODE_R / globalScale;
	const label = knownAccount(node.id);
	const isCreator = label === "Creator";

	if (isCreator) {
		nodeScale = nodeScale * 1.5;
	} else if (!node.participant && node.links.length < 3) {
		nodeScale = nodeScale * 0.4;
	}

	//
	//Create Background
	if (highlightNodes.value.has(node)) {
		// add ring just for highlighted nodes
		ctx.beginPath();
		ctx.arc(node.x, node.y, nodeScale * 1.25, 0, 2 * Math.PI, false);
		ctx.fillStyle =
			node === hoverNode.value
				? "rgba(255, 0, 0, 0.9)"
				: "rgba(255, 165, 0, 0.9)";
		ctx.fill();
	} else if (!node.participant && node.links.length < 3) {
		//
	} else {
		// add ring just for highlighted nodes
		ctx.beginPath();
		ctx.arc(node.x, node.y, nodeScale * 1.1, 0, 2 * Math.PI, false);
		ctx.fillStyle = "rgba(255, 255, 255, 0.7)";
		ctx.fill();
	}

	ctx.beginPath();
	if (isCreator) {
		ctx.fillStyle = "#A020F0";
		ctx.arc(node.x, node.y, nodeScale, 0, 2 * Math.PI, false);
	} else if (!node.participant && node.links.length < 3) {
		ctx.fillStyle = "rgba(255,255,255,0.6)";
		ctx.arc(node.x, node.y, nodeScale, 0, 2 * Math.PI, false);
	} else if (highlightNodes.value.size > 0 && !highlightNodes.value.has(node)) {
		ctx.fillStyle = hexToRgbA(node.color, 0.7);
		ctx.arc(node.x, node.y, nodeScale, 0, 2 * Math.PI, false);
	} else {
		ctx.fillStyle = node.color;
		ctx.arc(node.x, node.y, nodeScale, 0, 2 * Math.PI, false);
	}
	ctx.fill();

	if (!node.participant && node.links.length < 3) return;

	//Add label text on last layer
	const fontSize = 12 / globalScale;
	ctx.font = `${fontSize}px Sans-Serif`;
	ctx.textAlign = "center";
	ctx.textBaseline = "middle";
	ctx.fillStyle = "white";
	ctx.fillText(label, node.x, node.y);
}

function hexToRgbA(hex, opacity = 1) {
	let c;
	if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
		c = hex.substring(1).split("");
		if (c.length == 3) {
			c = [c[0], c[0], c[1], c[1], c[2], c[2]];
		}
		c = "0x" + c.join("");
		return (
			"rgba(" +
			[(c >> 16) & 255, (c >> 8) & 255, c & 255].join(",") +
			`,${opacity})`
		);
	}
	throw new Error("Bad Hex");
}
function observeVisibility(element, callback, threshold = 0.1) {
	if (!element) {
		console.error("Element not found!");
		return;
	}

	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				callback(entry.isIntersecting, entry);
			});
		},
		{
			root: null, // Observes relative to the viewport
			rootMargin: "0px",
			threshold: threshold, // Default: Trigger when 10% of the element is visible
		}
	);

	observer.observe(element);
	return observer; // Return observer in case you need to disconnect it later
}

// setup
onMounted(init);

return { __sfc: true,props, graph2D, graphData, el2D, hoverNode, highlightNodes, highlightLinks, NODE_R, fmtComp, init, fetchGraphData, processResponse, initGraph, buildDataObject, knownAccount, onNodeClick, onNodeHover, onLinkHover, nodeLabel, drawNode, hexToRgbA, observeVisibility, AccountAddress }
}

})